// Indicador de conectividade — barra inferior estilo Spotify (offline/reconectado).
//
// OFFLINE: mostra uma barra cinza fixa no rodapé (acima do menu no mobile), com
// um botão de fechar (X) pra não poluir a tela. Fechou → some, e reaparece se
// cair a conexão de novo. RECONECTAR: um flash verde "restaurada" que some
// sozinho. Mobile-first, PT-BR, sem alert() nativo (regras do CLAUDE.md).
//
// Autocontido e idempotente: injeta o próprio <style> UMA vez e se monta em
// document.body, funcionando em todas as páginas. É inicializado por
// `wireMoreSheet` (shared/nav.js), uma vez por página. Detecção via
// `navigator.onLine` + eventos `online`/`offline` (os mesmos que o WebView do
// Capacitor dispara), alinhado a core/offlineWrite.js.

let _wired = false;

const STYLE = `
.offline-bar{position:fixed;left:12px;right:12px;z-index:2000;
  bottom:calc(var(--mp-safe-bottom, env(safe-area-inset-bottom,0px)) + 68px);
  display:flex;align-items:center;gap:10px;
  padding:11px 12px 11px 16px;border-radius:12px;
  font-size:13px;line-height:1.3;font-weight:600;color:#fff;
  background:#2b2b2f;box-shadow:0 8px 24px rgba(0,0,0,.35);
  transform:translateY(calc(100% + 90px));opacity:0;pointer-events:none;
  transition:transform .3s ease,opacity .3s ease;}
.offline-bar.show{transform:translateY(0);opacity:1;pointer-events:auto;}
.offline-bar.is-back{background:#1c7d4d;}
.offline-bar-dot{width:8px;height:8px;border-radius:50%;background:#fff;flex:0 0 auto;opacity:.9;}
.offline-bar.is-offline .offline-bar-dot{animation:offline-pulse 1.4s ease-in-out infinite;}
@keyframes offline-pulse{0%,100%{opacity:.35}50%{opacity:1}}
.offline-bar-text{flex:1 1 auto;}
.offline-bar-close{flex:0 0 auto;width:40px;height:40px;margin:-6px -4px -6px 0;
  border:0;background:transparent;color:#fff;font-size:18px;line-height:1;cursor:pointer;
  display:flex;align-items:center;justify-content:center;border-radius:10px;}
.offline-bar-close:hover{background:rgba(255,255,255,.14);}
@media (min-width:768px){
  .offline-bar{left:auto;right:16px;max-width:380px;
    bottom:calc(env(safe-area-inset-bottom,0px) + 16px);}
}
`;

const OFFLINE_MSG = 'Você está offline — as mudanças sincronizam ao reconectar';
const BACK_MSG = 'Conexão restaurada — sincronizando';

/**
 * Monta e liga a barra de conectividade. Seguro chamar várias vezes (só age na
 * primeira). No-op fora do browser (SSR/testes de fonte).
 */
export function initOfflineBanner() {
  if (_wired || typeof document === 'undefined' || typeof window === 'undefined') return;
  _wired = true;

  if (!document.getElementById('offlineBannerStyle')) {
    const style = document.createElement('style');
    style.id = 'offlineBannerStyle';
    style.textContent = STYLE;
    document.head.appendChild(style);
  }

  const el = document.createElement('div');
  el.id = 'offlineBanner';
  el.className = 'offline-bar';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.innerHTML = '<span class="offline-bar-dot" aria-hidden="true"></span>'
    + '<span class="offline-bar-text"></span>'
    + '<button type="button" class="offline-bar-close" aria-label="Fechar aviso">✕</button>';
  const text = el.querySelector('.offline-bar-text');
  const closeBtn = el.querySelector('.offline-bar-close');

  let hideTimer = null;
  let wasOffline = false;
  let dismissed = false;

  function render() {
    const online = navigator.onLine !== false;
    if (!online) {
      if (dismissed) return; // usuário fechou; fica escondido até reconectar/cair de novo
      clearTimeout(hideTimer);
      wasOffline = true;
      el.classList.remove('is-back');
      el.classList.add('is-offline', 'show');
      text.textContent = OFFLINE_MSG;
    } else if (wasOffline) {
      wasOffline = false;
      el.classList.remove('is-offline');
      el.classList.add('is-back', 'show');
      text.textContent = BACK_MSG;
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => el.classList.remove('show', 'is-back'), 2600);
    } else {
      el.classList.remove('show', 'is-offline', 'is-back');
    }
  }

  closeBtn.addEventListener('click', () => {
    dismissed = true;
    el.classList.remove('show');
  });

  // Cair a conexão reseta o "fechado" — a barra volta a aparecer.
  window.addEventListener('offline', () => { dismissed = false; render(); });
  window.addEventListener('online', render);

  const mount = () => {
    if (!document.getElementById('offlineBanner')) document.body.appendChild(el);
    render();
  };
  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount, { once: true });
}
